import { tt, type HubTab } from '@agos/shared'

/**
 * *Správa rozvozu → Nastavenia rozvozu*, as tabs - FRONTEND.md §9.302.
 *
 * The owner: *"in Správa Rozvoz - Nastavenia Rozvozu, add more settings.. one for that QR code
 * payment settings, if its already somewhere, movve it there"*. It was somewhere - `/qr-payment`,
 * its own sidebar entry under a different section - so this is the move.
 *
 * **The guards are the folded sidebar entries' own**, asserted equal to them in the test beside this
 * file. That was the invariant worth holding for a fold: *a tab shows to exactly whoever the sidebar
 * entry it replaced showed to.* Both entries carried a tier gate as well as a permission -
 * `delivery_settings` and `qr_payment` are modules as well as flags - and matching the routes alone
 * would have widened who sees each screen, because neither route carried its tier gate then.
 * **§9.744 narrows the QR half on purpose**: the tab's own gate is still the old entry's, but it is
 * drawn inside a hub that now asks `delivery_settings` alone, so it shows to whoever holds both.
 *
 * **The hub is *Nastavenia rozvozu*, and the QR form lives inside it - §9.744, owner O114.** Until
 * then the hub route admitted `delivery_settings` *or* `qr_payment`, so that somebody holding only the
 * QR right kept the way in their old sidebar entry gave them. The owner reported the cost: *"Práva na
 * nastavenie rozvoz aj keď sú vypnuté vie nastavovať qr platby"* - un-ticking *Nastavenia rozvozu*
 * left the hub in the sidebar, opening on the QR form alone. So the hub asks `delivery_settings`, the
 * QR tab asks `qr_payment` **as well** (a tab is only ever drawn inside the hub), and the standalone
 * `/qr-payment` route asks both, plus the tab's module. Un-ticking *Nastavenia rozvozu* takes the QR
 * form with it, which is what the owner expected.
 *
 * **One residual case, stated rather than hidden.** The hub route still carries no tier gate: the two
 * tabs have different modules, and a gate on the depot's would take the QR tab from a franchise whose
 * tier has QR payment without the delivery settings module. A franchise holding the right with
 * **both** modules switched off therefore reaches the hub and gets its empty state instead of a
 * refusal. That is what the empty state is for.
 */
export const DELIVERY_TABS: readonly HubTab[] = [
  {
    // The depot address, which was the whole of this screen before the move. First because it is
    // what somebody opening *Nastavenia rozvozu* came for.
    id: 'depot',
    label: tt('Depot'),
    path: '/delivery-settings',
    guard: { permission: 'delivery_settings', franchiseModule: 'delivery_settings' },
  },
  {
    // §9.744 - drawn only inside the hub, so what it admits is the hub's `delivery_settings` and this
    // gate together; `/qr-payment` asks the same three things, which the test beside this file holds.
    id: 'qr',
    label: tt('QR platba'),
    path: '/qr-payment',
    guard: { permission: 'qr_payment', franchiseModule: 'qr_payment' },
  },
  {
    /* §9.766, owner O127 - *Platby zákazníkov*: which ways a customer may pay, and the two *Pravidlá pokladne* moved
     * here from *Platby online - archív*. **No route of its own**: it draws on the hub's, so a deep link is
     * `/delivery-settings?tab=customer-payments` and nothing new is reachable by path. Drawn to whoever opens the hub -
     * the switches are the checkout's public facts, so reading them asks nothing more - and saved by whoever holds
     * *Platby online* (`customer-payments.ts`). No tier gate: neither switch belongs to a module. */
    id: 'customer-payments',
    label: tt('Platby zákazníkov'),
    path: '/delivery-settings',
    guard: { permission: 'delivery_settings' },
  },
]
